/**
 * RIBAAG Premium Frontend Experience
 * High-performance, clean architecture for luxury streetwear.
 * Handles: Wishlist, Recently Viewed, Sticky ATC, Cart Drawer Sync, Added-to-Bag Toasts.
 * Zero fake urgency countdowns, zero fake social proof popups, zero exit-intent spam.
 */
(function() {
  "use strict";

  const bodyData = document.body.dataset;
  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches || document.body.classList.contains("rbg-motion-none");

  // 1. Wishlist Storage & Toggle
  const WISHLIST_KEY = "rbg-wishlist";
  function getWishlist() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
    } catch {
      return [];
    }
  }

  function initWishlist() {
    if (bodyData.rbgWishlist !== "true") return;
    const saved = getWishlist();
    document.querySelectorAll(".rbg-wish").forEach(btn => {
      const id = btn.dataset.rbgWish;
      if (!id || btn.dataset.rbgWishBound) return;
      btn.dataset.rbgWishBound = "true";
      if (saved.includes(id)) {
        btn.classList.add("is-saved");
      }
      btn.addEventListener("click", e => {
        e.preventDefault();
        e.stopPropagation();
        const list = getWishlist();
        const idx = list.indexOf(id);
        if (idx === -1) {
          list.push(id);
          btn.classList.add("is-saved");
        } else {
          list.splice(idx, 1);
          btn.classList.remove("is-saved");
        }
        try {
          localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
        } catch {}
      });
    });
  }

  // 2. Recently Viewed Products Recorder
  const RECENT_KEY = "rbg-recent";
  function initRecentlyViewed() {
    const metaEl = document.getElementById("rbg-product-meta");
    if (metaEl) {
      try {
        const meta = JSON.parse(metaEl.textContent);
        let recent = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]").filter(item => item.url !== meta.url);
        recent.unshift(meta);
        localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, 12)));
      } catch {}
    }

    renderRecentlyViewed(metaEl);
  }

  // Renders recently viewed products with the theme's own product card
  function renderRecentlyViewed(metaEl) {
    const section = document.querySelector("[data-rbg-recent-section]");
    if (!section) return;
    const shopifySection = section.closest(".shopify-section");
    const hide = () => { if (shopifySection) shopifySection.style.display = "none"; };

    let recent = [];
    try {
      recent = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    } catch {
      recent = [];
    }
    let currentUrl = null;
    if (metaEl) {
      try { currentUrl = JSON.parse(metaEl.textContent).url; } catch {}
    }
    const limit = parseInt(section.dataset.limit, 10) || 4;
    const urls = recent
      .map(item => item && item.url)
      .filter(url => url && url !== currentUrl)
      .slice(0, limit);
    if (!urls.length) { hide(); return; }

    const loadCard = url => {
      const sep = url.indexOf("?") > -1 ? "&" : "?";
      return fetch(url + sep + "section_id=ribaag-recently-viewed")
        .then(res => (res.ok ? res.text() : null))
        .then(html => {
          if (!html) return null;
          const doc = new DOMParser().parseFromString(html, "text/html");
          const tpl = doc.querySelector("template[data-rbg-recent-card]");
          return tpl ? tpl.innerHTML : null;
        })
        .catch(() => null);
    };

    Promise.all(urls.map(loadCard)).then(cards => {
      const found = cards.filter(Boolean);
      const list = section.querySelector("[data-rbg-recent-list]");
      const wrap = section.querySelector("[data-rbg-recent-wrap]");
      if (!found.length || !list || !wrap) { hide(); return; }
      list.innerHTML = found.join("");
      wrap.hidden = false;
      initWishlist();
      if (shopifySection) shopifySection.style.display = "";
    });
  }

  // 3. Real "Added to Bag" Toast Notification
  function showAddedToBagToast(title, imageUrl) {
    if (document.querySelector("cart-drawer") && typeof window.updateAndOpenCartDrawer === "function") {
      // Cart drawer handles the active confirmation
      return;
    }
    if (!title) return;

    const now = Date.now();
    if (title === window._lastToastTitle && (now - (window._lastToastTime || 0)) < 800) {
      return;
    }
    window._lastToastTime = now;
    window._lastToastTitle = title;

    let container = document.getElementById("rbg-toast-container");
    if (!container) {
      container = document.createElement("div");
      container.id = "rbg-toast-container";
      container.style.cssText = "position: fixed; left: 1.4rem; bottom: 1.4rem; z-index: 10000; display: flex; flex-direction: column-reverse; gap: 1rem; pointer-events: none;";
      document.body.appendChild(container);
    }

    const toast = document.createElement("div");
    toast.className = "rbg-toast";
    toast.setAttribute("role", "status");
    toast.setAttribute("aria-live", "polite");
    toast.style.cssText = "position: relative !important; left: auto !important; bottom: auto !important; pointer-events: auto !important; margin: 0 !important;";
    toast.innerHTML = `
      <img class="rbg-toast__img" src="${imageUrl || ''}" alt="" width="44" height="54">
      <div class="rbg-toast__txt">
        <b>ADDED TO BAG</b>
        <span data-rbg-toast-line>${title}</span>
        <div class="rbg-toast__time">Just now</div>
      </div>
    `;

    toast.addEventListener("click", () => {
      toast.classList.remove("is-in");
      setTimeout(() => toast.remove(), 450);
    });

    container.appendChild(toast);
    setTimeout(() => toast.classList.add("is-in"), 10);
    setTimeout(() => {
      toast.classList.remove("is-in");
      setTimeout(() => toast.remove(), 450);
    }, 4000);
  }

  // 4. Cart Drawer Sync & Re-render
  async function updateAndOpenCartDrawer() {
    try {
      const rootUrl = window.Shopify?.routes?.root || "/";
      const res = await fetch(`${rootUrl}cart?sections=cart-drawer,cart-icon-bubble`);
      const sections = await res.json();
      const cartDrawerHtml = sections["cart-drawer"];
      const bubbleHtml = sections["cart-icon-bubble"];
      const cartDrawer = document.querySelector("cart-drawer");
      const bubble = document.querySelector("#cart-icon-bubble");

      if (cartDrawer) {
        cartDrawer.classList.remove("is-empty");
        const inner = cartDrawer.querySelector(".drawer__inner");
        if (inner) inner.classList.remove("is-empty");

        if (typeof cartDrawer.renderContents === "function" && cartDrawerHtml) {
          cartDrawer.renderContents({ sections });
        } else {
          if (cartDrawerHtml) {
            const parsed = new DOMParser().parseFromString(cartDrawerHtml, "text/html");
            const newContent = parsed.querySelector("#CartDrawer");
            const currentContent = document.querySelector("#CartDrawer");
            if (currentContent && newContent) {
              currentContent.innerHTML = newContent.innerHTML;
            }
          }
          if (bubbleHtml) {
            const parsed = new DOMParser().parseFromString(bubbleHtml, "text/html");
            const targetBubble = document.querySelector("#cart-icon-bubble");
            if (targetBubble) {
              const node = parsed.querySelector(".shopify-section") || parsed.body;
              if (node) targetBubble.innerHTML = node.innerHTML;
            }
          }
          const overlay = cartDrawer.querySelector("#CartDrawer-Overlay");
          if (overlay) {
            overlay.addEventListener("click", () => {
              typeof cartDrawer.close === "function" ? cartDrawer.close() : cartDrawer.classList.remove("active", "animate");
              document.body.classList.remove("overflow-hidden");
            });
          }
          typeof cartDrawer.open === "function" ? cartDrawer.open(bubble) : cartDrawer.classList.add("animate", "active");
        }
        cartDrawer.classList.add("animate", "active");
        document.body.classList.add("overflow-hidden");
      }
    } catch (err) {
      console.error("[RIBAAG] Error updating cart drawer:", err);
    }
  }

  // 5. Sticky Add-to-Cart Bar + Back-to-Top Coordinator
  function initStickyAtc() {
    const bar = document.getElementById("rbg-atc");
    if (!bar) return;
    const submitBtn = document.querySelector(".product-form__submit, product-form");
    const footer = document.querySelector(".rbg-footer, footer, .footer, #shopify-section-footer, [id*='footer']");
    const backToTopBtn = document.getElementById("rbg-back-to-top");

    let isPastSubmit = false;
    let isFooterVisible = false;

    function updateStickyAtcVisibility() {
      const shouldShow = isPastSubmit && !isFooterVisible;
      bar.classList.toggle("is-visible", shouldShow);
      bar.setAttribute("aria-hidden", shouldShow ? "false" : "true");
      document.body.classList.toggle("rbg-sticky-atc-active", shouldShow);

      if (backToTopBtn) {
        backToTopBtn.classList.toggle("rbg-above-sticky-atc", shouldShow);
      }
    }

    if (submitBtn && "IntersectionObserver" in window) {
      // 1. Observe product form submit button
      const submitObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          isPastSubmit = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          updateStickyAtcVisibility();
        });
      }, { threshold: 0 });
      submitObserver.observe(submitBtn);

      // 2. Observe footer so sticky bar hides when user reaches footer
      if (footer) {
        const footerObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            isFooterVisible = entry.isIntersecting;
            updateStickyAtcVisibility();
          });
        }, { threshold: 0 });
        footerObserver.observe(footer);
      }

      // 3. Fast-scroll listener for immediate real-time sync
      window.addEventListener("scroll", () => {
        const submitRect = submitBtn.getBoundingClientRect();
        isPastSubmit = submitRect.bottom < 0;

        if (footer) {
          const footerRect = footer.getBoundingClientRect();
          isFooterVisible = footerRect.top < window.innerHeight;
        } else {
          isFooterVisible = false;
        }

        updateStickyAtcVisibility();
      }, { passive: true });

      // 4. Initial state evaluation
      const initialSubmitRect = submitBtn.getBoundingClientRect();
      isPastSubmit = initialSubmitRect.bottom < 0;
      if (footer) {
        const initialFooterRect = footer.getBoundingClientRect();
        isFooterVisible = initialFooterRect.top < window.innerHeight;
      }
      updateStickyAtcVisibility();

      const barBtn = bar.querySelector(".rbg-atc__btn");
      if (barBtn) {
        barBtn.addEventListener("click", () => {
          const mainBtn = document.querySelector(".product-form__submit");
          if (mainBtn) {
            mainBtn.click();
          } else {
            window.scrollTo({ top: 0, behavior: prefersReducedMotion ? "auto" : "smooth" });
          }
        });
      }
    }
  }

  // Init Core Architecture
  function init() {
    initWishlist();
    initRecentlyViewed();
    initStickyAtc();
  }

  // Global bindings
  window.showAddedToBagToast = showAddedToBagToast;
  window.updateAndOpenCartDrawer = updateAndOpenCartDrawer;
  window._lastClickedAtcBtn = null;

  document.addEventListener("click", e => {
    const btn = e.target.closest('[data-add-to-cart], [data-qv-add-to-cart], .rbg-checkout__add-btn, .rbg-quickview-submit-btn, [name="add"], .product-form__submit');
    if (btn) window._lastClickedAtcBtn = btn;
  }, true);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

// Razorpay Magic Checkout Integration
window.rbgTriggerCheckout = window.rbgTriggerCheckout || async function() {
  if (document.getElementById("RazorpayMagicCheckoutConfig") && typeof window.openRzpCheckout !== "function") {
    for (let i = 0; i < 8 && typeof window.openRzpCheckout !== "function"; i++) {
      await new Promise(r => setTimeout(r, 100));
    }
  }
  if (typeof window.openRzpCheckout === "function") {
    try {
      window.openRzpCheckout();
      return true;
    } catch (e) {
      console.warn("[RIBAAG] openRzpCheckout failed:", e);
    }
  }
  const magicBtn = document.querySelector('magic-checkout-btn:not([page-type="product"]), #magic-checkout-btn, .magic-checkout-btn, [data-testid="magic-checkout-button"], .rzp-magic-btn__buy-cart, magic-checkout-btn');
  if (magicBtn) {
    try {
      magicBtn.click();
      return true;
    } catch (e) {
      console.warn("[RIBAAG] magicBtn click failed:", e);
    }
  }
  const drawerCheckout = document.querySelector('#CartDrawer-Checkout, button[name="checkout"]');
  if (drawerCheckout && typeof window.Razorpay !== "undefined") {
    try {
      drawerCheckout.click();
      return true;
    } catch (e) {
      console.warn("[RIBAAG] drawerCheckout click failed:", e);
    }
  }
  const root = window.Shopify?.routes?.root || "/";
  window.location.href = root + "checkout";
  return false;
};
